import { useEffect, useRef } from 'react';
import { cn } from '../../utils/cn';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastData {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
}

interface ToastProps {
  toast: ToastData;
  onDismiss: (id: string) => void;
}

const variantStyles: Record<ToastVariant, string> = {
  success: 'bg-success-light text-success dark:bg-success/15 dark:text-success',
  error: 'bg-error-light text-error dark:bg-error/15 dark:text-error',
  warning: 'bg-warning-light text-warning dark:bg-warning/15 dark:text-warning',
  info: 'bg-info-light text-info dark:bg-info/15 dark:text-info',
};

function SuccessIcon(): React.ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        fill="currentColor"
      />
    </svg>
  );
}

function ErrorIcon(): React.ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
        fill="currentColor"
      />
    </svg>
  );
}

function WarningIcon(): React.ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
        fill="currentColor"
      />
    </svg>
  );
}

function InfoIcon(): React.ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        fill="currentColor"
      />
    </svg>
  );
}

const variantIcons: Record<ToastVariant, () => React.ReactElement> = {
  success: SuccessIcon,
  error: ErrorIcon,
  warning: WarningIcon,
  info: InfoIcon,
};

export function Toast({ toast: toastData, onDismiss }: ToastProps): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const Icon = variantIcons[toastData.variant];
  const isUrgent = toastData.variant === 'error' || toastData.variant === 'warning';

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Trigger enter animation
    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });
  }, []);

  const handleDismiss = (): void => {
    const el = ref.current;
    if (!el) {
      onDismiss(toastData.id);
      return;
    }

    // Exit animation
    el.style.opacity = '0';
    el.style.transform = 'translateY(8px)';
    setTimeout(() => onDismiss(toastData.id), 150);
  };

  return (
    <div
      ref={ref}
      role={isUrgent ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-card p-4 shadow-card-elevated',
        'opacity-0 translate-y-2 transition-all duration-normal ease-out',
        'motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0',
        variantStyles[toastData.variant],
      )}
      style={{ opacity: 0, transform: 'translateY(8px)' }}
    >
      <span className="mt-0.5 shrink-0">
        <Icon />
      </span>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-5">{toastData.title}</p>
        {toastData.description ? (
          <p className="mt-1 text-sm leading-5 opacity-80">{toastData.description}</p>
        ) : null}
      </div>

      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 rounded-lg p-1 opacity-60 transition-opacity hover:opacity-100 focus-visible:outline-2 focus-visible:outline-current"
        aria-label="Dismiss notification"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z"
            fill="currentColor"
          />
        </svg>
      </button>
    </div>
  );
}
